import {
  envIsIntegration,
  envIsLocal,
  serverEnableBackendGrouping,
  serverFlagDynamicBinSizing,
} from '../../config';
import {RampIdType} from '../../generated/graphql';
import {getDatadogRum} from '../datadog/datadogRum';
import {
  useEntityRampFlag,
  useOrgRampFlag,
  useUserOrOrgRampFlag,
  useUserRampFlag,
} from '../useRampFlag';

const DD_RUM = getDatadogRum();

export const useRampFlagUseBackendSmoothing = (entityName: string) => {
  const isDeploymentEnabled = useOrgRampFlag(
    'backend_smoothing_deployment',
    RampIdType.OrgId
  );
  const isUserEnabled = useUserRampFlag(
    'backend_smoothing_user',
    RampIdType.UserName
  );
  const isEntityEnabled = useEntityRampFlag(
    entityName,
    'backend_smoothing_entity',
    RampIdType.EntityName
  );
  if (envIsLocal) {
    return isDeploymentEnabled ?? false;
  }
  const value = isUserEnabled || isEntityEnabled;
  DD_RUM.setGlobalContextProperty('backend_smoothing', value);
  return value;
};

export const useRampFlagUseBackendGrouping = (entityName: string) => {
  const isOrgEnabled = useOrgRampFlag(
    'use-backend-grouping-org',
    RampIdType.OrgName
  );
  const isUserEnabled = useUserRampFlag(
    'use-backend-grouping-user',
    RampIdType.UserName
  );
  const isEntityEnabled = useEntityRampFlag(
    entityName,
    'use-backend-grouping-entity',
    RampIdType.EntityName
  );
  if (envIsLocal) {
    return serverEnableBackendGrouping();
  }
  const value = isOrgEnabled || isUserEnabled || isEntityEnabled;
  DD_RUM.setGlobalContextProperty('use_backend_grouping', value);
  return value;
};

export const useRampFlagNonMonotonicXAxis = (entityName: string) => {
  const userOrOrgFlag = useUserOrOrgRampFlag(
    'non-monotonic-x-axis-user',
    'non-monotonic-x-axis-org'
  );
  const entityFlag = useEntityRampFlag(
    entityName,
    'non-monotonic-x-axis-entity',
    RampIdType.EntityName
  );

  return userOrOrgFlag || entityFlag;
};

export const useRampFlagLineConstructionProfiling = (entityName: string) => {
  return useEntityRampFlag(
    entityName,
    'line-construction-profiling',
    RampIdType.EntityName
  );
};

export const useDynamicBinSizing = (entityName: string) => {
  const isStatsigFlagEnabled = useStatsigFlagDynamicBinSizing(entityName);
  const isDeprecatedRampFlagEnabled =
    useRampFlagLowResolutionFullFidelityQueries(entityName);
  const isServerFlagEnabled = serverFlagDynamicBinSizing();

  const value =
    isStatsigFlagEnabled || isDeprecatedRampFlagEnabled || isServerFlagEnabled;
  DD_RUM.setGlobalContextProperty('dynamic_bin_sizing', value);
  return value;
};

export const useStatsigFlagDynamicBinSizing = (entityName: string) => {
  const userOrOrgFlag = useUserOrOrgRampFlag(
    'dynamic_bin_sizing_user',
    'dynamic_bin_sizing_org'
  );

  const entityFlag = useEntityRampFlag(
    entityName,
    'dynamic_bin_sizing_entity',
    RampIdType.EntityName
  );

  const value = userOrOrgFlag || entityFlag;
  return value;
};

// TO BE DEPRECATED
export const useRampFlagLowResolutionFullFidelityQueries = (
  entityName: string
) => {
  // Check existing ramp flags (to be deprecated)
  const userOrOrgFlag = useUserOrOrgRampFlag(
    'low-resolution-queries-user',
    'low-resolution-queries-org'
  );

  const entityFlag = useEntityRampFlag(
    entityName,
    'low-resolution-queries-entity',
    RampIdType.EntityName
  );

  const value = userOrOrgFlag || entityFlag;
  DD_RUM.setGlobalContextProperty('low_resolution_queries', value);
  return value;
};

export const useRampFlagFullFidelityRanges = (entityName: string) => {
  const isDeploymentEnabled = useOrgRampFlag(
    'full_fidelity_ranges_deployment',
    RampIdType.OrgId
  );
  const isEntityEnabled = useEntityRampFlag(
    entityName,
    'full_fidelity_ranges_entity',
    RampIdType.EntityName
  );
  const isUserEnabled = useUserRampFlag(
    'full_fidelity_ranges_user',
    RampIdType.UserName
  );
  if (envIsLocal) {
    return isDeploymentEnabled ?? true;
  }
  return isEntityEnabled || isUserEnabled;
};

/**
 * This currently doesn't support an entity flag because I didn't want to do the work to thread and entity value into the check. It probably won't be that hard but we're just experimenting currently.
 */
export const useRampFlagFullFidelityRunsPerChart = () => {
  const userOrOrg = useUserOrOrgRampFlag(
    'full_fidelity_run_per_chart_user',
    'full_fidelity_run_per_chart_org'
  );
  DD_RUM.setGlobalContextProperty('full_fidelity_run_per_chart', userOrOrg);
  return userOrOrg;
};

/**
 * Combined feature flag for higher full-screen line plot max runs.
 * True if either the org-level or user-level Statsig gate is enabled
 * (backend: increase_full_screen_max_runs_org, increase_full_screen_max_runs_user).
 */
export const useRampFlagIncreaseFullScreenMaxRuns = () => {
  const isUserEnabled = useUserRampFlag(
    'increase_full_screen_max_runs_user',
    RampIdType.UserId
  );
  const isOrgEnabled = useOrgRampFlag(
    'increase_full_screen_max_runs_org',
    RampIdType.OrgName
  );
  const value = isUserEnabled || isOrgEnabled || false;
  DD_RUM.setGlobalContextProperty('increase_full_screen_max_runs', value);
  return value;
};

export const useStatsigGateBackendLineConstruction = (entityName: string) => {
  const isUserEnabled = useUserRampFlag(
    'backend_line_construction_user',
    RampIdType.UserId
  );
  const isOrgEnabled = useOrgRampFlag(
    'backend_line_construction_org',
    RampIdType.OrgId
  );
  const isEntityEnabled = useEntityRampFlag(
    entityName,
    'backend_line_construction_entity',
    RampIdType.EntityId
  );
  const value = isUserEnabled || isOrgEnabled || isEntityEnabled;
  DD_RUM.setGlobalContextProperty('backend_line_construction_user', value);
  return value;
};

export const useStatsigGateBackendLineConstructionSmoothing = (
  entityName: string
) => {
  const value = useEntityRampFlag(
    entityName,
    'backend_line_construction_smoothing',
    RampIdType.EntityName
  );
  DD_RUM.setGlobalContextProperty('backend_line_construction_smoothing', value);
  return value;
};

export const useStatsigGateBackendLineConstructionTimeUnits = (
  entityName: string
) => {
  const value = useEntityRampFlag(
    entityName,
    'backend_line_construction_time_units',
    RampIdType.EntityName
  );
  DD_RUM.setGlobalContextProperty(
    'backend_line_construction_time_units',
    value
  );
  return value;
};

export const useStatsigGateBackendLineConstructionReports = (
  entityName: string
) => {
  const isOrgEnabled = useOrgRampFlag(
    'backend_line_construction_reports_org',
    RampIdType.OrgId
  );
  const isEntityEnabled = useEntityRampFlag(
    entityName,
    'backend_line_construction_reports_entity',
    RampIdType.EntityId
  );
  const value = isOrgEnabled || isEntityEnabled;
  DD_RUM.setGlobalContextProperty('backend_line_construction_reports', value);
  return value;
};

export const useStatsigGateCombinedMetricsRunsLimitUI = (
  entityName: string
) => {
  const isEntityEnabled = useEntityRampFlag(
    entityName,
    'combined_metrics_runs_limit_ui',
    RampIdType.EntityId
  );

  if (envIsIntegration) {
    return false;
  }

  const value = isEntityEnabled;
  DD_RUM.setGlobalContextProperty('combined_metrics_runs_limit_ui', value);
  return value;
};

export const useRampFlagClientsideDynamicTimeouts = (
  entityName: string | undefined
) => {
  const isEnabled = useEntityRampFlag(
    entityName,
    'clientside_dynamic_timeouts_entity',
    RampIdType.EntityId
  );
  DD_RUM.setGlobalContextProperty(
    'clientside_dynamic_timeouts_entity',
    isEnabled
  );
  return isEnabled;
};
